import axios from 'axios';

// Create axios instance with base URL
// Use / as fallback to make requests relative to current host (through Caddy proxy)
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/',
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/chat';
    }
    return Promise.reject(error);
  }
);

// API Service object
const api = {
  // Auth endpoints
  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    const response = await apiClient.post('/api/auth/login', formData);
    return response.data;
  },

  register: async (username, password) => {
    const response = await apiClient.post('/api/auth/register', {
      username,
      password,
    });
    return response.data;
  },

  getMe: async () => {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  },

  // Chat endpoints
  createSession: async () => {
    const response = await apiClient.post('/api/chat/session');
    return response.data;
  },

  getSessions: async () => {
    const response = await apiClient.get('/api/chat/sessions');
    return response.data;
  },

  getSession: async (sessionId) => {
    const response = await apiClient.get(`/api/chat/sessions/${sessionId}`);
    return response.data;
  },

  getCurrentSession: async () => {
    const response = await apiClient.get('/api/chat/session/current');
    return response.data;
  },

  sendMessage: async (message, sessionId = null) => {
    const response = await apiClient.post('/api/chat/message', {
      message,
      session_id: sessionId,
    });
    return response.data;
  },

  // RAG endpoints
  uploadDocument: async (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/api/rag/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },

  // SSE for indexing progress
  subscribeToIndexingProgress: (docId, onProgress, onError) => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_URL || '/';
    const eventSource = new EventSource(
      `${baseUrl}/api/rag/upload/progress/${docId}?token=${token}`
    );

    eventSource.addEventListener('progress', (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onProgress) {
          onProgress(data);
        }
      } catch (e) {
        console.error('Error parsing progress data:', e);
      }
    });

    eventSource.addEventListener('error', async (error) => {
      console.error('SSE connection error:', error);
      eventSource.close();

      // Try to get document status to determine what happened
      if (onError) {
        try {
          const statusResponse = await apiClient.get(`/api/rag/documents/${docId}/status`);
          onError(null, statusResponse.data);
        } catch (statusError) {
          onError(error, null);
        }
      }
    });

    return eventSource;
  },

  // Check document status
  getDocumentStatus: async (docId) => {
    const response = await apiClient.get(`/api/rag/documents/${docId}/status`);
    return response.data;
  },

  getDocuments: async () => {
    const response = await apiClient.get('/api/rag/documents');
    return response.data;
  },

  deleteDocument: async (docId) => {
    const response = await apiClient.delete(`/api/rag/documents/${docId}`);
    return response.data;
  },

  clearAllDocuments: async () => {
    const response = await apiClient.delete('/api/rag/documents');
    return response.data;
  },

  getDocumentUrl: async (docId) => {
    const response = await apiClient.get(`/api/rag/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  getPdfBlob: async (docId) => {
    const response = await apiClient.get(`/api/rag/documents/${docId}/download`, {
      responseType: 'arraybuffer',
    });
    return new Blob([response.data], { type: 'application/pdf' });
  },

  downloadDocument: async (docId, filename) => {
    const response = await apiClient.get(`/api/rag/documents/${docId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  // STT endpoint
  transcribeAudio: async (audioBlob) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');
    const response = await apiClient.post('/api/stt/transcribe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.text;
  },

  // TTS endpoint
  synthesizeSpeech: async (text) => {
    const response = await apiClient.post(
      '/api/tts/synthesize',
      { text },
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Memory endpoints
  getProfile: async () => {
    const response = await apiClient.get('/api/chat/profile');
    return response.data;
  },

  getSessionSummary: async (sessionId) => {
    const response = await apiClient.get(`/api/chat/sessions/${sessionId}/summary`);
    return response.data;
  },

  endSession: async (sessionId) => {
    const response = await apiClient.put(`/api/chat/sessions/${sessionId}/end`);
    return response.data;
  },

  generateProfile: async () => {
    const response = await apiClient.post('/api/chat/profile/generate');
    return response.data;
  },

  // Account deletion
  deleteAccount: async () => {
    const response = await apiClient.delete('/api/auth/delete-account');
    return response.data;
  },
};

export default api;
